import type { InvitationRole, PageState } from "./types";

export interface RenderLinks {
  appLink: string | null;
  storeLink: string | null;
  // Held only in memory for the lifetime of this render — never logged,
  // never sent anywhere. Its only two uses anywhere in this app are
  // building the same-origin "Open Daruvix" href (deepLinks.ts) and the
  // clipboard-copy button built below.
  fragmentSecret: string | null;
}

const ROLE_LABEL: Record<InvitationRole, string> = {
  caregiver: "Caregiver",
  nurse: "Nurse",
};

export function render(root: HTMLElement, state: PageState, links: RenderLinks): void {
  root.setAttribute("aria-live", "polite");
  root.innerHTML = "";
  root.appendChild(buildCard(state, links));
}

function buildCard(state: PageState, links: RenderLinks): HTMLElement {
  const card = el("div", "card");
  card.appendChild(buildLogo());
  card.appendChild(buildContent(state, links));
  card.appendChild(el("p", "privacy-note", "No medical information is shown on this page."));
  return card;
}

function buildContent(state: PageState, links: RenderLinks): HTMLElement {
  const content = el("div", "content");

  switch (state.kind) {
    case "loading":
      content.appendChild(el("p", "status", "Checking your invitation…"));
      return content;

    case "valid": {
      const role = ROLE_LABEL[state.preview.intended_role];
      content.appendChild(
        el("h1", "heading", `You've been invited to join a Daruvix Care Team as a ${role}.`),
      );
      content.appendChild(
        el(
          "p",
          "body",
          "Daruvix helps patients securely share selected care information with people they trust.",
        ),
      );
      content.appendChild(buildCtas(links, true));
      content.appendChild(buildInvitationCode(links.fragmentSecret));
      return content;
    }

    case "expired":
      content.appendChild(el("h1", "heading", "This invitation has expired."));
      content.appendChild(buildCtas(links, false));
      return content;

    case "revoked":
      content.appendChild(el("h1", "heading", "This invitation is no longer active."));
      content.appendChild(buildCtas(links, false));
      return content;

    case "already_used":
      content.appendChild(el("h1", "heading", "This invitation has already been accepted."));
      content.appendChild(buildCtas(links, false));
      return content;

    case "invalid":
      content.appendChild(el("h1", "heading", "This invitation link is invalid."));
      content.appendChild(buildCtas(links, false));
      return content;

    case "network_error":
      content.appendChild(
        el("h1", "heading", "We couldn't verify this invitation right now."),
      );
      content.appendChild(buildCtas(links, false));
      return content;
  }
}

// includeOpenDaruvix is false for every non-"valid" state: redemption
// only ever happens inside the authenticated app after this page has
// confirmed the invitation is actually usable, so offering a direct
// "open and redeem" action for an expired/revoked/used/invalid/unverified
// link would be misleading. "Get Daruvix" (plain app-store link, no
// invitation context) stays available everywhere — wanting the app is
// independent of this one invitation's fate.
function buildCtas(links: RenderLinks, includeOpenDaruvix: boolean): HTMLElement {
  const wrap = el("div", "ctas");
  if (includeOpenDaruvix && links.appLink) {
    const open = el("a", "btn btn-primary", "Open Daruvix") as HTMLAnchorElement;
    open.href = links.appLink;
    wrap.appendChild(open);
  }
  if (links.storeLink) {
    const store = el("a", "btn btn-secondary", "Get Daruvix") as HTMLAnchorElement;
    store.href = links.storeLink;
    store.target = "_blank";
    store.rel = "noopener noreferrer";
    wrap.appendChild(store);
  }
  return wrap;
}

// Only ever rendered on the "valid" state (see buildContent above) and
// only offers a copy action when a fragment secret actually exists —
// this is the SAME plaintext delivery secret from the URL fragment, not
// a newly minted token. It is the one thing this page lets the user
// carry manually into the app's own "have an invitation code?" entry
// point, for the guaranteed iOS-not-installed / deep-link-failed
// fallback; this page itself never validates or redeems it.
function buildInvitationCode(secret: string | null): HTMLElement {
  const wrap = el("div", "invitation-code");
  wrap.appendChild(el("p", "trouble-label", "Having trouble?"));

  if (!secret) {
    wrap.appendChild(
      el(
        "p",
        "code-unavailable",
        "No invitation code was found in this link. Please check your email again.",
      ),
    );
    return wrap;
  }

  const button = el("button", "btn btn-ghost", "Copy invitation code") as HTMLButtonElement;
  button.type = "button";
  button.addEventListener("click", () => {
    navigator.clipboard?.writeText(secret).then(
      () => {
        button.textContent = "Copied!";
        setTimeout(() => (button.textContent = "Copy invitation code"), 2000);
      },
      () => {
        button.textContent = "Copy failed — select manually";
      },
    );
  });
  wrap.appendChild(button);
  return wrap;
}

function buildLogo(): HTMLElement {
  return el("div", "logo", "Daruvix");
}

function el(tag: string, className: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
